import { makeApiProvider } from '@pflegenavi/shared-frontend/platform';
import { createContext } from 'react';
import type { IReceiptBatchApi } from './api';
import { ReceiptBatchApi } from './api';
import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import type { ReceiptBatchControllerResultTypes } from '@pflegenavi/service-provider-shared/api-endpoints';

const ApiContext = createContext<IReceiptBatchApi | undefined>(undefined);
const { useApi: useReceiptBatchApi, ApiProvider: ReceiptBatchApiProvider } =
  makeApiProvider({
    name: 'ReceiptBatch',
    ApiContext,
    newApi: (tenantId, auth, apiUrl) =>
      new ReceiptBatchApi(tenantId, auth, apiUrl),
  });

export { ReceiptBatchApiProvider };

export {
  isSubmitReceiptBatchServiceProviderUserIncompleteException,
  isSubmitReceiptBatchResidentInfoIncompleteException,
  isSubmitReceiptBatchNursingHomeInfoIncompleteException,
  isSubmitReceiptBatchServiceProviderInfoIncompleteException,
  isSubmitReceiptBatchDateError,
} from './api';

export const DELETE_RECEIPT_BATCH_ENTRY_KEY = ['deleteReceiptBatchEntry'];
export const DELETE_RECEIPT_BATCH_KEY = ['deleteReceiptBatch'];
export const PATCH_RECEIPT_BATCH_KEY = ['patchReceiptBatch'];
export const POST_RECEIPT_BATCH_KEY = ['postReceiptBatch'];
export const RETRACT_RECEIPT_BATCH_KEY = ['retractReceiptBatch'];
export const SUBMIT_RECEIPT_BATCH_KEY = ['submitReceiptBatch'];
export const CANCEL_RECEIPT_BATCH_ENTRY_KEY = ['cancelReceiptBatchEntry'];
const GENERATE_RECEIPT_BATCH_PREVIEW_PDF_KEY = [
  'generateReceiptBatchPreviewPdf',
];
export const useGenerateReceiptPdf = (): UseMutationResult<
  ReceiptBatchControllerResultTypes['generateReceiptPdf'],
  unknown,
  {
    batchId: string;
    entryId: string;
  }
> => {
  const api = useReceiptBatchApi();
  const result = useMutation<
    ReceiptBatchControllerResultTypes['generateReceiptPdf'],
    unknown,
    {
      batchId: string;
      entryId: string;
    }
  >({
    mutationKey: RETRACT_RECEIPT_BATCH_KEY,
    mutationFn: (data: { batchId: string; entryId: string }) =>
      api.generateReceiptBatchEntryPdf({
        params: { batchId: data.batchId, entryId: data.entryId },
      }),
  });
  return result;
};

export const useGenerateReceiptBatchPdf = (): UseMutationResult<
  ReceiptBatchControllerResultTypes['generateReceiptBatchPdf'],
  unknown,
  {
    batchId: string;
  }
> => {
  const api = useReceiptBatchApi();
  const result = useMutation<
    ReceiptBatchControllerResultTypes['generateReceiptBatchPdf'],
    unknown,
    {
      batchId: string;
    }
  >({
    mutationKey: GENERATE_RECEIPT_BATCH_PREVIEW_PDF_KEY,
    mutationFn: (data: { batchId: string }) =>
      api.generateReceiptBatchPdf({
        params: { batchId: data.batchId },
      }),
  });
  return result;
};
